import { getExtensionValue, resolveId } from "@medplum/core";
import { Observation, Reference, Resource } from "@medplum/fhirtypes";

import { parseIsoInstant } from "@/encounters/utils/questionnaire-response-instant";
import { Extensions } from "@/fhir/enums/extensions";
import { getQuestionnaireResponseIdFromObservationDerivedFrom } from "@/utils/medplum";
import {
  compareObservationsByRecencyThenIdDesc,
  pickObservationNewerByVersionIdThenRecencyThenId,
} from "@/utils/observation-recency";

/** Guards against malformed previous-version extension cycles in history bundles. */
const MAX_OBSERVATION_VERSION_CHAIN_DEPTH = 64;

function getPrimaryObservationFormCode(observation: Observation): string | undefined {
  return observation.code?.coding?.[0]?.code;
}

function getObservationAmendMaterializationInstant(observation: Observation): number | undefined {
  return parseIsoInstant(observation.meta?.lastUpdated) ?? parseIsoInstant(observation.issued);
}

function observationsShareDerivedFromQuestionnaireResponse(
  first: Observation,
  second: Observation,
): boolean {
  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    first.derivedFrom,
  );
  if (!questionnaireResponseId) {
    return false;
  }
  return (
    getQuestionnaireResponseIdFromObservationDerivedFrom(second.derivedFrom) ===
    questionnaireResponseId
  );
}

/**
 * Returns the Observation id referenced by `ObservationPreviousVersion`, if present.
 *
 * @param observation - Flowsheet observation that may point at an older version.
 * @returns Previous version resource id, or undefined when not linked.
 */
export function getObservationPreviousVersionTargetId(
  observation: Observation,
): string | undefined {
  const previousVersionReference = getExtensionValue(
    observation,
    Extensions.ObservationPreviousVersion,
  ) as Reference<Resource> | undefined;

  return resolveId(previousVersionReference) ?? undefined;
}

/**
 * Resolves the pre-amend Observation id for a flowsheet row superseded during amend.
 *
 * Production edits usually keep the same QuestionnaireResponse (`derivedFrom`) while the bot
 * cancels the prior Observation and creates a new one without copying `ObservationPreviousVersion`
 * onto the new row.
 */
export function resolveObservationAmendPredecessorId(
  observation: Observation,
  allObservations: ReadonlyArray<Observation>,
): string | undefined {
  const previousVersionTargetId = getObservationPreviousVersionTargetId(observation);
  if (previousVersionTargetId) {
    return previousVersionTargetId;
  }

  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    observation.derivedFrom,
  );
  if (!questionnaireResponseId) {
    return undefined;
  }

  const leafMaterializationInstant = getObservationAmendMaterializationInstant(observation);
  const formCode = getPrimaryObservationFormCode(observation);

  const cancelledPredecessors = allObservations.filter((candidate) => {
    if (candidate.id === observation.id || candidate.status !== "cancelled") {
      return false;
    }
    if (
      getQuestionnaireResponseIdFromObservationDerivedFrom(candidate.derivedFrom) !==
      questionnaireResponseId
    ) {
      return false;
    }
    if (formCode && getPrimaryObservationFormCode(candidate) !== formCode) {
      return false;
    }
    return true;
  });

  if (cancelledPredecessors.length === 0) {
    return undefined;
  }

  const sortedPredecessors = [...cancelledPredecessors].sort(
    compareObservationsByRecencyThenIdDesc,
  );

  if (leafMaterializationInstant === undefined) {
    return sortedPredecessors[0]?.id;
  }

  const predecessorBeforeLeaf = sortedPredecessors.find((candidate) => {
    const candidateInstant = getObservationAmendMaterializationInstant(candidate);
    return candidateInstant !== undefined && candidateInstant <= leafMaterializationInstant;
  });

  return predecessorBeforeLeaf?.id ?? sortedPredecessors[0]?.id;
}

/**
 * Resolves the predecessor Observation for amend version diff (extension or bot supersession).
 */
export function resolveObservationAmendPredecessor(
  observation: Observation,
  observationsById: ReadonlyMap<string, Observation>,
  allObservations: ReadonlyArray<Observation>,
): Observation | undefined {
  const predecessorId = resolveObservationAmendPredecessorId(observation, allObservations);
  if (!predecessorId) {
    return undefined;
  }
  return observationsById.get(predecessorId);
}

/**
 * Collects the observation and each older version reachable via `ObservationPreviousVersion`.
 *
 * @param observation - Starting row (typically the leaf referenced from `hasMember`).
 * @param observationsById - Encounter flowsheet observations keyed by id.
 * @returns Newest-first chain members (starting with `observation`).
 */
export function collectObservationVersionChainMembers(
  observation: Observation,
  observationsById: ReadonlyMap<string, Observation>,
): Observation[] {
  const members: Observation[] = [];
  const visitedIds = new Set<string>();
  let current: Observation | undefined = observation;

  for (let depth = 0; depth < MAX_OBSERVATION_VERSION_CHAIN_DEPTH && current; depth += 1) {
    if (current.id) {
      if (visitedIds.has(current.id)) {
        break;
      }
      visitedIds.add(current.id);
    }
    members.push(current);
    const previousVersionId = resolveObservationAmendPredecessorId(current, [
      ...observationsById.values(),
    ]);
    if (!previousVersionId) {
      break;
    }
    current = observationsById.get(previousVersionId);
  }

  return members;
}

function isActiveSuccessorForCancelledDerivedFromRow(
  cancelledObservation: Observation,
  candidate: Observation,
): boolean {
  return (
    candidate.status !== "cancelled" &&
    candidate.id !== cancelledObservation.id &&
    observationsShareDerivedFromQuestionnaireResponse(cancelledObservation, candidate) &&
    (!getPrimaryObservationFormCode(cancelledObservation) ||
      getPrimaryObservationFormCode(candidate) ===
        getPrimaryObservationFormCode(cancelledObservation))
  );
}

function resolveActiveSuccessorForCancelledDerivedFromRow(
  cancelledObservation: Observation,
  allObservations: ReadonlyArray<Observation>,
): Observation | undefined {
  const successors = allObservations.filter((candidate) =>
    isActiveSuccessorForCancelledDerivedFromRow(cancelledObservation, candidate),
  );

  if (successors.length === 0) {
    return undefined;
  }

  return successors.reduce((newest, candidate) =>
    pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
  );
}

/**
 * Walks forward along amend supersession links and returns the newest amend leaf
 * for a stage `hasMember` reference (often still pointing at the pre-amend or cancelled id).
 *
 * @param rootObservationId - Observation id from the labor stage `hasMember` reference.
 * @param allObservations - Encounter labor observations used to resolve successors.
 * @returns Newest successor when the row was edited during amend, otherwise the root row.
 */
export function resolveLatestObservationVersionLeaf(
  rootObservationId: string,
  allObservations: ReadonlyArray<Observation>,
): Observation | undefined {
  const observationsById = new Map(
    allObservations
      .filter((observation): observation is Observation & { id: string } => Boolean(observation.id))
      .map((observation) => [observation.id, observation]),
  );

  const rootLeaf = observationsById.get(rootObservationId);
  if (!rootLeaf) {
    return undefined;
  }

  let leaf: Observation = rootLeaf;
  if (leaf.status === "cancelled") {
    const activeSuccessor = resolveActiveSuccessorForCancelledDerivedFromRow(leaf, allObservations);
    if (activeSuccessor) {
      leaf = activeSuccessor;
    }
  }

  const leafFormCode = getPrimaryObservationFormCode(leaf);

  const sameQuestionnaireActiveRows = allObservations.filter(
    (candidate) =>
      candidate.status !== "cancelled" &&
      observationsShareDerivedFromQuestionnaireResponse(leaf, candidate) &&
      (!leafFormCode || getPrimaryObservationFormCode(candidate) === leafFormCode),
  );
  if (sameQuestionnaireActiveRows.length > 0) {
    leaf = [leaf, ...sameQuestionnaireActiveRows].reduce(
      (newest: Observation, candidate: Observation) =>
        pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
    );
  }

  for (let depth = 0; depth < MAX_OBSERVATION_VERSION_CHAIN_DEPTH; depth += 1) {
    const leafId = leaf.id;
    if (!leafId) {
      return leaf;
    }

    const children: Observation[] = allObservations.filter(
      (candidate) => getObservationPreviousVersionTargetId(candidate) === leafId,
    );

    if (children.length === 0) {
      return leaf;
    }

    leaf = children.reduce((newest: Observation, candidate: Observation) =>
      pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
    );
  }

  return leaf;
}

/**
 * Whether this row supersedes a prior flowsheet Observation (extension link or bot cancel/create).
 */
export function isObservationSupersedingPriorFlowsheetRow(
  observation: Observation,
  allObservations: ReadonlyArray<Observation>,
): boolean {
  return resolveObservationAmendPredecessorId(observation, allObservations) !== undefined;
}
